import { Alert } from '@mui/material';
import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PortalErrorBoundary } from '~/components/PortalErrorBoundary';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';
import { EmployeeModuleTabs } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/components/EmployeeModuleTabs';
import { EmployeeModuleTabPath_Rewarding } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/RewardingModule/data';
import { useEmployeeRewardingModule } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/RewardingModule/useEmployeeRewardingModule';
import { convertRewardingModuleTabsToEmployeeProposalDialogTabs } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/data';
import { AcknowledgementsTab } from '~/scenes/RewardingModule/components/Tabs/AcknowledgementsTab';
import { BankAccountTab } from '~/scenes/RewardingModule/components/Tabs/BankAccountTab';
import { BenefitsTab } from '~/scenes/RewardingModule/components/Tabs/BenefitsTab';
import { BillableHoursTab } from '~/scenes/RewardingModule/components/Tabs/BillableHoursTab';
import { CustomDataTab } from '~/scenes/RewardingModule/components/Tabs/CustomDataTab';
import { DocumentsTab } from '~/scenes/RewardingModule/components/Tabs/DocumentsTab';
import { GeneralTab } from '~/scenes/RewardingModule/components/Tabs/GeneralTab';
import { IncentivesTab } from '~/scenes/RewardingModule/components/Tabs/IncentivesTab';
import { SummaryTab } from '~/scenes/RewardingModule/components/Tabs/SummaryTab';
import { WorkTimeTab } from '~/scenes/RewardingModule/components/Tabs/WorkTimeTab';

type PropsType = {
  hasFullAccess: boolean;
  modulePath: string;
};

export const EmployeeRewardingModule = (props: PropsType) => {
  const { hasFullAccess, modulePath } = props;

  const { isLoading, tabs, enabledTabPaths, defaultTab } =
    useEmployeeRewardingModule({
      hasFullAccess,
      modulePath,
    });

  const enabledProposalDialogTabs = useMemo(
    () =>
      convertRewardingModuleTabsToEmployeeProposalDialogTabs(enabledTabPaths),
    [enabledTabPaths],
  );

  const { t } = useTranslation();

  const renderTabContent = (tab: string) => {
    if (!enabledTabPaths.map((p) => p.toString()).includes(tab)) {
      return <Alert severity={'error'}>{t('laNoAuthorization')}</Alert>;
    }

    switch (tab) {
      case EmployeeModuleTabPath_Rewarding.General:
        return (
          <GeneralTab enabledProposalDialogTabs={enabledProposalDialogTabs} />
        );
      case EmployeeModuleTabPath_Rewarding.Incentives:
        return (
          <IncentivesTab
            enabledProposalDialogTabs={enabledProposalDialogTabs}
          />
        );
      case EmployeeModuleTabPath_Rewarding.BillableHours:
        return (
          <BillableHoursTab
            enabledProposalDialogTabs={enabledProposalDialogTabs}
          />
        );
      case EmployeeModuleTabPath_Rewarding.Benefits:
        return (
          <BenefitsTab enabledProposalDialogTabs={enabledProposalDialogTabs} />
        );
      case EmployeeModuleTabPath_Rewarding.Acknowledgements:
        return <AcknowledgementsTab />;
      case EmployeeModuleTabPath_Rewarding.BankAccount:
        return <BankAccountTab />;
      case EmployeeModuleTabPath_Rewarding.Documents:
        return <DocumentsTab />;
      case EmployeeModuleTabPath_Rewarding.CustomData:
        return <CustomDataTab />;
      case EmployeeModuleTabPath_Rewarding.Summary:
        return <SummaryTab />;
      case EmployeeModuleTabPath_Rewarding.WorkTime:
        return <WorkTimeTab />;
      default:
        return <></>;
    }
  };

  if (isLoading) {
    return <LoadingView />;
  }

  return (
    <EmployeeModuleTabs
      defaultTab={defaultTab}
      enabledTabPaths={enabledTabPaths}
      tabs={tabs}
      render={(tab) => (
        <PortalErrorBoundary>{renderTabContent(tab)}</PortalErrorBoundary>
      )}
    />
  );
};
